import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Globe, Lock, Users } from 'lucide-react';
import type { GroupList, RecordAccess, SetRecordAccess, Visibility } from '@mnc/contracts';
import { api } from '../../lib/api';
import { describeError } from '../../lib/errors';
import { pick } from '../../i18n';
import { Badge, Field, SearchableSelect } from '../ui';
import { UserPicker } from './UserPicker';

type Choice = 'entity' | 'groups' | 'people' | 'private';

/**
 * Record visibility (phase-1-scope §1.0b): Entity · Groups · People · Only me as one control.
 * Groups and People both store `restricted`; they combine on the same record.
 */
export function VisibilityControl({ ownerType, ownerId, compact }: { ownerType: string; ownerId: string; compact?: boolean }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const key = ['record-access', ownerType, ownerId];
  const access = useQuery({ queryKey: key, queryFn: () => api.get<RecordAccess>(`/api/records/${ownerType}/${ownerId}/access`) });
  const groups = useQuery({ queryKey: ['groups', 'directory'], queryFn: () => api.get<GroupList>('/api/groups/directory'), staleTime: 60_000 });
  const [choice, setChoice] = useState<Choice>('entity');
  const [groupCodes, setGroupCodes] = useState<string[]>([]);
  const [userIds, setUserIds] = useState<string[]>([]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!access.data) return;
    const a = access.data;
    setChoice(a.visibility === 'restricted' ? (a.users.length && !a.groups.length ? 'people' : 'groups') : a.visibility);
    setGroupCodes(a.groups);
    setUserIds(a.users);
    setDirty(false);
  }, [access.data]);

  const save = useMutation({
    mutationFn: (body: SetRecordAccess) => api.put<RecordAccess>(`/api/records/${ownerType}/${ownerId}/access`, body),
    onSuccess: (data) => {
      qc.setQueryData(key, data);
      setDirty(false);
      toast.success(t('records.saved'));
    },
    onError: (e) => toast.error(describeError(e, t)),
  });

  const groupOptions = useMemo(() => (groups.data?.items ?? []).map((g) => ({ value: g.code, label: pick(i18n.language, g.name_en, g.name_ar) })), [groups.data, i18n.language]);
  if (!access.data) return null;
  const a = access.data;
  const editable = a.can_change;
  const visibility: Visibility = choice === 'entity' ? 'entity' : choice === 'private' ? 'private' : 'restricted';
  const options: Array<{ value: Choice; label: string }> = [
    { value: 'entity', label: t('records.entity') },
    { value: 'groups', label: t('records.groups') },
    { value: 'people', label: t('records.people') },
    { value: 'private', label: t('records.onlyMe') },
  ];
  const Icon = visibility === 'entity' ? Globe : visibility === 'private' ? Lock : Users;

  return (
    <div className={compact ? '' : 'card p-4'}>
      <div className="mb-2 flex items-center gap-2">
        <Icon size={16} className="text-neutral-500" />
        <span className="text-sm font-semibold">{t('records.whoCanSee')}</span>
        {a.parent_visibility && a.parent_visibility !== 'entity' && <Badge tone="warn">{t('records.inheritsParent', { visibility: t(`records.${a.parent_visibility}`) })}</Badge>}
      </div>
      <div className="grid gap-3">
        <SearchableSelect disabled={!editable} value={choice} onChange={(v) => { setChoice(v as Choice); setDirty(true); }} options={options} />
        {(choice === 'groups' || choice === 'people') && (
          <>
            <Field label={t('records.groups')}>
              <SearchableSelect multiple disabled={!editable} value={groupCodes} onChange={(v) => { setGroupCodes(v as string[]); setDirty(true); }} options={groupOptions} placeholder={t('records.pickGroups')} />
            </Field>
            <Field label={t('records.people')} hint={t('records.assigneesAlwaysSee')}>
              <UserPicker disabled={!editable} value={userIds} onChange={(v) => { setUserIds(v); setDirty(true); }} />
            </Field>
          </>
        )}
        {choice === 'private' && <p className="text-xs text-neutral-500">{t('records.onlyMeHint')}</p>}
        {editable && dirty && (
          <div>
            <button type="button" className="btn-primary" disabled={save.isPending} onClick={() => save.mutate({ visibility, groups: visibility === 'restricted' ? groupCodes : [], users: visibility === 'restricted' ? userIds : [] })}>
              {t('app.save')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Small read-only badge for lists and cards. */
export function VisibilityBadge({ visibility }: { visibility: Visibility }) {
  const { t } = useTranslation();
  if (visibility === 'entity') return null;
  return <Badge tone={visibility === 'private' ? 'danger' : 'warn'}>{visibility === 'private' ? t('records.onlyMe') : t('records.restricted')}</Badge>;
}
